import {
  pideQuienRecomendo,
  type OrigenDeCandidato,
} from '@/features/rh/lib/origenDelCandidato'
/**
 * Reglas del alta manual de candidato (RH-05).
 *
 * Viven aparte de la pantalla para poder probarlas sin montar el modal, y
 * porque son las mismas que valida el servidor: si alguna se relaja aquí, el
 * back sigue rechazando, y al revés el usuario se entera antes de mandar.
 */

/**
 * Las columnas del tablero en las que se puede dar de alta.
 *
 * «Aprobado por RH» no está (SRH-307): a esa columna se llega guardando los
 * resultados de la entrevista, que es lo único que la deja aprobada. Dando de
 * alta ahí directo el candidato nacía sin entrevista y no aparecía al agendar
 * con el solicitante. Es la misma lista que acepta el servidor.
 */
export const ETAPAS_DE_ALTA = [
  { valor: 'pendiente', etiqueta: 'Sin revisión' },
  { valor: 'en_proceso', etiqueta: 'En proceso' },
  { valor: 'seleccionado', etiqueta: 'Seleccionado' },
] as const

export type EtapaDeAlta = (typeof ETAPAS_DE_ALTA)[number]['valor']

export const EDAD_MINIMA = 18
export const EDAD_MAXIMA = 100
/** El servidor pide diez dígitos, sin importar cómo vengan separados. */
const DIGITOS_DE_TELEFONO = 10

export const errorDeNombre = (valor: string): string | null => {
  if (!valor.trim()) return 'El nombre es obligatorio'
  if (valor.trim().length > 255) return 'El nombre no puede pasar de 255 caracteres'
  return null
}

export const errorDeProfesion = (valor: string): string | null => {
  if (!valor.trim()) return 'La profesión es obligatoria'
  if (valor.trim().length > 255) return 'La profesión no puede pasar de 255 caracteres'
  return null
}

/**
 * La edad llega del input como cadena. `Number('')` es 0, así que el campo
 * vacío tiene que atajarse antes de convertir o se reporta como edad inválida
 * en vez de como campo faltante.
 */
export const errorDeEdad = (valor: string): string | null => {
  if (!valor.trim()) return 'La edad es obligatoria'
  const edad = Number(valor)
  if (!Number.isInteger(edad)) return 'La edad debe ser un número entero'
  if (edad < EDAD_MINIMA) return `La edad mínima es ${EDAD_MINIMA} años`
  if (edad > EDAD_MAXIMA) return `La edad máxima es ${EDAD_MAXIMA} años`
  return null
}

export const errorDeTelefono = (valor: string): string | null => {
  if (!valor.trim()) return 'El teléfono es obligatorio'
  if (valor.trim().length > 20) return 'El teléfono no puede pasar de 20 caracteres'
  const digitos = (valor.match(/\d/g) ?? []).length
  if (digitos < DIGITOS_DE_TELEFONO) {
    return `El teléfono debe llevar al menos ${DIGITOS_DE_TELEFONO} dígitos`
  }
  return null
}

/** Opcional: vacío es válido, y solo se revisa el formato cuando hay algo. */
export const errorDeCorreo = (valor: string): string | null => {
  const limpio = valor.trim()
  if (!limpio) return null
  if (limpio.length > 160) return 'El correo no puede pasar de 160 caracteres'
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(limpio)) {
    return 'El correo no tiene un formato válido'
  }
  return null
}

export const errorDeVacante = (valor: string): string | null =>
  valor ? null : 'Elige la vacante'

export const errorDeEtapa = (valor: string): string | null =>
  ETAPAS_DE_ALTA.some((e) => e.valor === valor) ? null : 'Elige la etapa'

export interface CamposDelAlta {
  jobRequestId: string
  status: string
  fullName: string
  age: string
  profesion: string
  phone: string
  email: string
  /** Origen declarado (RH-06). Vacio = no se capturo, y es valido. */
  sourceId: string
  referredByText: string
  /**
   * Lo mismo que pide el formulario publico, pero opcional (RH-05).
   *
   * RH da de alta a quien llega por otra via y no siempre trae el expediente
   * completo; lo que si trae no deberia perderse por no tener donde ponerlo.
   * Viajan como texto porque son campos de formulario: la conversion a numero
   * se hace al enviar.
   */
  experienceYears: string
  experienceDescription: string
  certificaciones: string
  residenceCity: string
  salaryExpectation: string
  /** La URL que devuelve S3 despues de subir el archivo. */
  cvUrl: string
  /** La evaluación, obligatoria al dar de alta en Seleccionado (SRH-309). */
  evaluationUrl: string
}

/**
 * Todos los errores del formulario.
 *
 * Solo se asignan las claves que de verdad fallaron: una clave con `undefined`
 * también cuenta en `Object.keys`, y con eso el formulario quedaría trabado sin
 * mostrar ningún mensaje.
 */
/**
 * El unico error del origen es dejar sin nombre una recomendacion (RH-06).
 *
 * Necesita el catalogo porque cual valor es «recomendacion» no se sabe por su
 * nombre sino por su bandera, y el catalogo se administra. Si no llega la
 * lista —todavia cargando— no se inventa un error: el backend valida igual.
 */
export const errorDeOrigen = (
  campos: Pick<CamposDelAlta, 'sourceId' | 'referredByText'>,
  origenes: OrigenDeCandidato[],
): string | null => {
  if (!pideQuienRecomendo(origenes, Number(campos.sourceId))) return null
  return campos.referredByText.trim()
    ? null
    : 'Indica quién recomendó al candidato'
}

/**
 * Los datos opcionales se revisan solo cuando traen algo.
 *
 * Los limites son los mismos del servidor: si aqui pasaran y alla no, el
 * formulario mandaria algo que vuelve como 400 sin señalar el campo.
 */
export const errorDeExperiencia = (valor: string): string | null => {
  const limpio = valor.trim()
  if (!limpio) return null
  const anios = Number(limpio)
  if (!Number.isInteger(anios)) return 'Los años de experiencia deben ser un número entero'
  if (anios < 0) return 'Los años de experiencia no pueden ser negativos'
  if (anios > 50) return 'Los años de experiencia no pueden pasar de 50'
  return null
}

export const errorDeSueldo = (valor: string): string | null => {
  const limpio = valor.trim()
  if (!limpio) return null
  const monto = Number(limpio)
  if (Number.isNaN(monto)) return 'El sueldo deseado no es un monto válido'
  if (monto < 0) return 'El sueldo deseado no puede ser negativo'
  if (monto > 99_999_999.99) return 'El sueldo deseado es demasiado alto'
  return null
}

export const errorDeCertificaciones = (valor: string): string | null =>
  valor.trim().length > 500
    ? 'Las certificaciones no pueden pasar de 500 caracteres'
    : null

export const errorDeCiudad = (valor: string): string | null =>
  valor.trim().length > 120 ? 'La ciudad no puede pasar de 120 caracteres' : null

export const erroresDelAlta = (
  campos: CamposDelAlta,
  origenes: OrigenDeCandidato[] = [],
): Record<string, string> => {
  const errores: Record<string, string> = {}
  const revisiones: Array<[keyof CamposDelAlta, string | null]> = [
    ['jobRequestId', errorDeVacante(campos.jobRequestId)],
    ['status', errorDeEtapa(campos.status)],
    ['fullName', errorDeNombre(campos.fullName)],
    ['age', errorDeEdad(campos.age)],
    ['profesion', errorDeProfesion(campos.profesion)],
    ['phone', errorDeTelefono(campos.phone)],
    ['email', errorDeCorreo(campos.email)],
    ['referredByText', errorDeOrigen(campos, origenes)],
    ['experienceYears', errorDeExperiencia(campos.experienceYears)],
    ['salaryExpectation', errorDeSueldo(campos.salaryExpectation)],
    ['certificaciones', errorDeCertificaciones(campos.certificaciones)],
    ['residenceCity', errorDeCiudad(campos.residenceCity)],
  ]
  for (const [campo, error] of revisiones) {
    if (error) errores[campo] = error
  }
  return errores
}
